import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { EMPTY, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { ToastService } from '@libs/src/toast/toast.service';
import { UserOnboardingErrorDialogService } from '@main-client/src/app/user-onboarding/user-onboard-error-dialog/user-onboarding-error-dialog.service';
import { UserOnboardingService } from '@main-client/src/app/user-onboarding/user-onboarding.service';

import { fadeInEnter } from '@libs/src/animations/animations';
import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import {
  Account,
  AccountGroupMembership,
  AccountSegmentSelection,
} from '@libs/src/models/account.model';
import {
  Group,
  GroupCustomSegment,
  GroupHiddenCustomSegment,
} from '@libs/src/models/group.model';
import {
  ProgramMembership,
  ProgramMembershipStatus,
} from '@libs/src/models/program-membership.model';
import {
  getSelectionsForSegment,
  getUserSelectedNodesForCustomSegment,
  updateSegmentSelections,
} from '@libs/src/segment-selections/segment-selections.helper';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import {
  UpdateUserGroup,
  UpdateUserPendingGroup,
} from '@libs/src/user/user.actions';
import { getMembership } from '@main-client/src/app/account/account.helpers';
import { IAppState } from '@main-client/src/app/app.state';
import { GROUP_JOIN_TYPE } from '@main-client/src/app/group-join/group-join-type.enum';
import { isValidToJoinGroup } from '@main-client/src/app/group-join/group-join.helper';
import { USER_ONBOARDING_ROUTE_PATHS } from '@main-client/src/app/user-onboarding/user-onboarding.helper';

import { isAnyAdmin } from '../../user/user.helpers';

import { LocaleManagerModalService } from '../../locale-manager/locale-manager-modal.service';
import { LocaleManagerService } from '../../locale-manager/locale-manager.service';

import castArray from 'lodash-es/castArray';
import isEmpty from 'lodash-es/isEmpty';
import map from 'lodash-es/map';
import pick from 'lodash-es/pick';

interface InteractionConfig {
  description: string;
  disabled: boolean;
  fieldName?: string;
  iconClass: string;
  id: string;
  title: string;
  tooltipText?: string;
}

@Component({
  animations: [fadeInEnter],
  providers: [DestroySubscriptionsService],
  selector: 'app-user-onboarding-hub-preferences-view',
  templateUrl: './user-onboarding-hub-preferences-view.component.html',
})
export class UserOnboardingHubPreferencesViewComponent
  implements OnChanges, OnInit
{
  @Input() group: Group;
  @Input() membership: AccountGroupMembership;
  @Input() preregistrationMembership: AccountGroupMembership;
  private _programMemberships: ProgramMembership[];
  @Input()
  set programMemberships(programMembership: ProgramMembership[]) {
    this._programMemberships = programMembership;
    this.createProgramMembershipForm(programMembership);
  }
  get programMemberships() {
    return this._programMemberships;
  }
  @Input() tenant: Tenant;
  @Input() user: Account;
  @Input() viewMode: USER_ONBOARDING_ROUTE_PATHS;
  @Input() numberOfSteps: number;
  @Output() navigateToNextView = new EventEmitter<Group>();
  readonly membershipFieldsToCopy: (keyof AccountGroupMembership)[] = [
    'hidden_segment_selections',
    'industries',
    'inviteable',
    'match_exclusions',
    'offerings',
    'opted_into_matchmaking',
    'role',
    'searchable',
    'seeking',
    'segment_selections',
  ];
  USER_ONBOARDING_ROUTE_PATHS = USER_ONBOARDING_ROUTE_PATHS;
  successPrompts = [
    $localize`:@@userOnboardingHubPreferencesView.successPrompt1:You're a valuable add to the community!`,
    $localize`:@@userOnboardingHubPreferencesView.successPrompt3:Thanks!`,
    $localize`:@@userOnboardingHubPreferencesView.successPrompt4:Okay!`,
    $localize`:@@userOnboardingHubPreferencesView.successPrompt5:Great!`,
    $localize`:@@userOnboardingHubPreferencesView.successPrompt6:Got it!`,
    $localize`:@@userOnboardingHubPreferencesView.noted:Noted!`,
    $localize`:@@userOnboardingHubPreferencesView.confirmed:Received!`,
    $localize`:@@userOnboardingHubPreferencesView.understood:Understood!`,
  ];
  segmentsForm: UntypedFormGroup;
  goalsForm = this.fb.group({
    offerings: [[]],
    seeking: [[]],
  });
  hints = {
    customSegments: [] as any,
    hiddenCustomSegments: [] as any,
    role: '',
  };
  interactionsForm: UntypedFormGroup = this.fb.group({
    inviteable: [],
    invitedDevPros: this.fb.array([]),
    opted_into_matchmaking: [],
    searchable: [],
  });
  interestsForm = this.fb.control([]);
  isLoading = false;
  interactionsConfig: InteractionConfig[];
  requiredCustomSegments: GroupCustomSegment[];
  formSubmissionError$ = new Subject<void>();
  resetFormOnChange = true;

  constructor(
    private readonly accountBackend: AccountBackend,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly fb: UntypedFormBuilder,
    private readonly localeManagerModalService: LocaleManagerModalService,
    private readonly localeManagerService: LocaleManagerService,
    private readonly store: Store<IAppState>,
    private readonly toastService: ToastService,
    private readonly userOnboardingService: UserOnboardingService,
    private readonly userOnboardingErrorDialogService: UserOnboardingErrorDialogService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.membership || changes.user) {
      if (this.resetFormOnChange || !this.segmentsForm) {
        this.initform();
      }
      this.interactionsForm.patchValue({
        inviteable: this.membership?.inviteable ?? true,
        opted_into_matchmaking: this.membership?.opted_into_matchmaking ?? true,
        searchable:
          this.membership?.searchable ??
          this.tenant.enabled_features?.members_directory ??
          true,
      });
      this.interestsForm.patchValue(this.membership?.industries ?? []);
      this.goalsForm.patchValue({
        offerings: this.membership?.offerings ?? [],
        seeking: this.membership?.seeking ?? [],
      });
      this.interactionsConfig = this.generateInteractionConfigs(
        this.group,
        this.tenant,
      );
      if (
        !this.localeManagerService.doesGroupSupportUserLocale(
          this.user,
          this.group,
        )
      ) {
        this.localeManagerModalService.openLanguageManager(this.user);
      }
    }
  }

  initform() {
    const customSegmentsControls = map(
      this.group.custom_segments,
      (segment: GroupCustomSegment) => {
        const selectionValue = getUserSelectedNodesForCustomSegment(
          this.membership?.segment_selections,
          segment,
          { multiSelectDefault: [], singleSelectDefault: '' },
        );
        return new FormControl({
          disabled:
            !(segment?.privacy_settings?.editable_by_self ?? true) &&
            !isAnyAdmin(this.user),
          value: selectionValue,
        });
      },
    );
    const hiddenCustomSegmentsControls = this.group.hidden_custom_segments?.map(
      (segment: GroupHiddenCustomSegment) => {
        const segmentSelections =
          this.membership?.hidden_segment_selections ?? [];
        return new FormControl({
          disabled:
            !(segment?.privacy_settings?.editable_by_self ?? true) &&
            !isAnyAdmin(this.user),
          value: getSelectionsForSegment(segmentSelections, segment),
        });
      },
    );
    this.segmentsForm = this.fb.group({
      customSegments: this.fb.array(customSegmentsControls),
      ...(hiddenCustomSegmentsControls?.length
        ? {
            hiddenCustomSegments: this.fb.array(hiddenCustomSegmentsControls),
          }
        : {}),
      role: [
        this.membership?.is_role_auto_selected
          ? ''
          : (this.membership?.role ?? ''),
      ],
    });
  }

  ngOnInit() {
    if (isEmpty(this.membership) && !isEmpty(this.preregistrationMembership)) {
      this.updateMembership(
        pick(this.preregistrationMembership, this.membershipFieldsToCopy),
      ).subscribe();
    }
    this.userOnboardingService
      .getRequiredCustomSegments(this.group)
      .subscribe((requiredCustomSegments) => {
        this.requiredCustomSegments = requiredCustomSegments;
      });
  }

  createProgramMembershipForm(memberships: ProgramMembership[]) {
    if (!memberships?.length) {
      return;
    }
    memberships.forEach((memberships) =>
      (this.interactionsForm.get('invitedDevPros') as FormArray).push(
        this.fb.group({
          inviteStatus: [memberships.status, this.isValidDevProStatus()],
        }),
      ),
    );
  }

  isValidDevProStatus() {
    return (control: FormControl) => {
      const status = ['active', 'declined'] satisfies ProgramMembershipStatus[];
      if (!status.includes(control.value)) {
        return { invalidStatus: true };
      }
      return null;
    };
  }

  getInvitedDevProsFormArray() {
    return this.interactionsForm.get('invitedDevPros') as FormArray;
  }

  roleChanged(role: string) {
    this.hints.role = '';
    return this.updateMembership({ role })
      .pipe(
        tap(() => {
          this.hints.role = this.successPrompts[0];
        }),
      )
      .subscribe();
  }

  updateSegmentSelections(
    segment: GroupCustomSegment,
    nodes: [string],
    index: number,
  ) {
    const nodesUpdate: string[] = castArray(nodes);
    const updatedSelections = updateSegmentSelections(
      this.membership?.segment_selections ?? [],
      segment,
      nodesUpdate,
    );
    this.hints.customSegments[index] = '';
    return this.updateMembership({ segment_selections: updatedSelections })
      .pipe(
        tap(() => {
          this.hints.customSegments[index] =
            this.successPrompts[(index + 1) % this.successPrompts.length];
        }),
      )
      .subscribe();
  }

  updateHiddenSegmentSelections(
    segment: GroupHiddenCustomSegment,
    nodes: string[],
    index: number,
  ) {
    const nodesUpdate: string[] = castArray(nodes);
    const updatedSelections = updateSegmentSelections(
      this.membership?.hidden_segment_selections ?? [],
      segment,
      nodesUpdate,
    );
    this.hints.hiddenCustomSegments[index] = '';
    return this.updateMembership({
      hidden_segment_selections: updatedSelections,
    })
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.hints.hiddenCustomSegments[index] =
            this.successPrompts[(index + 1) % this.successPrompts.length];
        }),
      )
      .subscribe();
  }

  updateMembership(update: { [fieldName: string]: any }) {
    this.isLoading = true;
    this.resetFormOnChange = true;
    const groupMembership = getMembership(this.user.groups, this.group._id);
    if (update?.segment_selections) {
      update.segment_selections = this.filterModifiedCustomSegments(
        this.group.custom_segments,
        update.segment_selections,
      );
    }
    if (
      isEmpty(groupMembership) &&
      !isValidToJoinGroup(this.user, this.group, '') &&
      GROUP_JOIN_TYPE.closed !== this.group?.join_type &&
      isEmpty(this.preregistrationMembership)
    ) {
      return this.accountBackend
        .putAccountPendingGroups(this.group, update)
        .pipe(
          tap((response: AccountGroupMembership[]) => {
            this.resetFormOnChange = false;
            this.store.dispatch(UpdateUserPendingGroup(response));
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastService.showError(getServerError(error));
            this.initform();
            return EMPTY;
          }),
          finalize(() => {
            this.isLoading = false;
          }),
        );
    }
    return this.accountBackend.putAccountGroups(this.group, update).pipe(
      catchError((error: HttpErrorResponse) => {
        this.toastService.showError(getServerError(error));
        this.initform();
        return EMPTY;
      }),
      tap((response: AccountGroupMembership[]) => {
        this.resetFormOnChange = false;
        this.store.dispatch(UpdateUserGroup(response));
      }),
      finalize(() => {
        this.isLoading = false;
      }),
    );
  }

  showErrorMessage() {
    this.userOnboardingErrorDialogService.openDialog();
  }

  updateItems(update: { [fieldName: string]: any }) {
    if (this.interactionsForm.get('invitedDevPros').invalid) {
      this.formSubmissionError$.next();
      this.showErrorMessage();
      return;
    }
    return this.updateMembership(update)
      .pipe(tap(() => this.navigateToNextView.emit(this.group)))
      .subscribe();
  }

  patchForm(form: UntypedFormGroup, fieldName: string, value: any) {
    form.patchValue({ [fieldName]: value });
  }

  generateInteractionConfigs(
    group: Group,
    tenant: Tenant,
  ): InteractionConfig[] {
    return [
      ...(false === group.features_enabled?.introductions
        ? []
        : [
            {
              description: $localize`:@@userOnboardingHubPreferencesView.configDescription.introductions:Get an introduction to another member for an informal conversation.`,
              disabled: false,
              fieldName: 'opted_into_matchmaking',
              iconClass: 'waving_hand',
              id: 'introductions',
              title: 'Introductions',
            },
          ]),
      ...(group.development_programs_enabled
        ? [
            {
              description: $localize`:@@userOnboardingHubPreferencesView.configDescription.devPrograms:Join Development Programs to expand your opportunities.`,
              disabled: true,
              iconClass: 'handshake',
              id: 'devPrograms',
              title: 'Development Programs',
              tooltipText: $localize`:@@userOnboardingHubPreferencesView.configTooltip.devpro:This can't be unselected. If you're invited to a program, you can choose if you want to join.`,
            },
          ]
        : []),
      ...(tenant.enabled_features?.events &&
      (group.features_enabled?.events ?? true)
        ? [
            {
              description: $localize`:@@userOnboardingHubPreferencesView.configDescription.officeHours:Small group sessions hosted by a leader or subject matter expert.`,
              disabled: true,
              fieldName: '',
              iconClass: 'campaign',
              id: 'officeHours',
              title: 'Office Hours',
              tooltipText: $localize`:@@userOnboardingHubPreferencesView.configTooltip.events:You'll always have access to Office Hours that's relevant to you. This can't be unchecked.`,
            },
          ]
        : []),
      ...((tenant.enabled_features?.members_directory ?? true)
        ? [
            {
              description: $localize`:@@userOnboardingHubPreferencesView.configDescription.search:Make your profile searchable in directory search.`,
              disabled: false,
              fieldName: 'searchable',
              iconClass: 'person_search',
              id: 'search',
              title: 'Directory search',
            },
          ]
        : []),
      ...(!tenant.enabled_features?.conversations
        ? []
        : [
            {
              description: $localize`:@@userOnboardingHubPreferencesView.configDescription.directMessage:Be open to other members to start a conversation with you via direct message.`,
              disabled: false,
              fieldName: 'inviteable',
              iconClass: 'sms',
              id: 'directMessage',
              title: 'Direct messaging',
            },
          ]),
    ];
  }

  isMembershipValid() {
    return (
      this.requiredCustomSegments &&
      this.userOnboardingService.isMembershipValid(
        this.membership,
        this.requiredCustomSegments,
      )
    );
  }

  filterModifiedCustomSegments(
    groupCustomSegments: GroupCustomSegment[],
    updateSegments: AccountSegmentSelection[],
  ) {
    return updateSegments.filter((segment) =>
      groupCustomSegments.some((groupSegment) => {
        return (
          segment.custom_segment === groupSegment._id &&
          (
            (segment.node && [segment.node]) ??
            segment.multiselected_nodes ??
            []
          ).every((updateNode) =>
            groupSegment.nodes.some((node) => node.name === updateNode),
          )
        );
      }),
    );
  }
}
